import { useMemo } from 'react';
import { JSONSchemaObjectWith } from '../../../util/jsonschemaTypes';
import { makeUID } from '../../../util/useUID';
import { FieldPathId } from '@rjsf/utils';

export type TabBase = {
  uid: string;
  title: string;
  depth: number;
  type: 'tabLeaf' | 'tabGroup' | 'tabRoot';
  path: TabPath;
  parent?: TabGroup;
  items?: Tab[];
  itemsIndex?: Record<string, Tab>;
};

export type TabLeaf = TabBase & {
  fieldPathId: FieldPathId;
  type: 'tabLeaf';
  leafIndex: number;
  fieldName: string;
  items?: undefined;
  itemsIndex?: undefined;
  parent: TabGroup | TabRoot;
};

export type TabGroup = TabBase & {
  type: 'tabGroup';
  items: NonNullable<TabBase['items']>;
  itemsIndex: NonNullable<TabBase['itemsIndex']>;
  parent: TabGroup | TabRoot;
};

export type TabRoot = TabBase & {
  type: 'tabRoot';
  items: NonNullable<TabBase['items']>;
  itemsIndex: NonNullable<TabBase['itemsIndex']>;
  leafMap: Record<string, TabLeaf>;
  leafTabs: TabLeaf[];
  parent?: undefined;
};

export type Tab = TabLeaf | TabGroup;

export type TabGroupsJSONSchema = JSONSchemaObjectWith<{
  properties: Record<
    string,
    JSONSchemaObjectWith<{
      'ui:tabGroup'?: string | string[];
      'ui:tabGroupKey'?: string;
      'ui:tabGroupPath'?: string[];
      'ui:tabGroupIcon'?: string;
      'ui:tabGroupIconColor'?: string;
    }>
  >;
}>;

export type UseTabGroupsProps = {
  canBeAsTabPanel: boolean;
  asGroupedTabs: boolean;
  schema: TabGroupsJSONSchema;
  properties: unknown;
  fieldPathId: FieldPathId;
};

const tabGroupPathObjectSymbol = Symbol('tabGroupPathObject');

function useTabGroups({ canBeAsTabPanel, asGroupedTabs, schema, properties, fieldPathId }: UseTabGroupsProps) {
  const root = useMemo(() => {
    let root: TabRoot;
    if (!canBeAsTabPanel || !asGroupedTabs) {
      root = buildFlatTabs(schema, fieldPathId);
    } else {
      const { root: _root, allTabGroups, tabGroupIndex } = buildTabGroups(schema);
      root = _root;
      addTabsToTabGroups(schema, tabGroupIndex, root, fieldPathId);
      sortTabGroups(allTabGroups);
    }

    tagLeafTabs(root);

    return root;
  }, [canBeAsTabPanel, asGroupedTabs, properties, schema, fieldPathId]);

  return root;
}

function buildFlatTabs(schema: TabGroupsJSONSchema, fieldPathId: FieldPathId) {
  const root = build.tabRoot();
  Object.entries(schema.properties).forEach(([fieldName, fieldSchema]) => {
    build.tabLeaf({
      title: fieldSchema['title'] || fieldName,
      fieldName,
      parent: root,
      root,
      fieldPathId: { $id: `${fieldPathId.$id}_${fieldName}`, path: [...fieldPathId.path, fieldName] },
    });
  });
  return root;
}

const build = {
  tabRoot: (): TabRoot => ({
    uid: makeUID('tr', '', '-', true),
    title: 'root',
    type: 'tabRoot',
    depth: 0,
    path: TabPath.fromComponents([]),
    items: [],
    itemsIndex: {},
    leafMap: {},
    leafTabs: [],
  }),
  tabGroup: ({ title, parent }: { title: string; parent: TabGroup | TabRoot }) => {
    const tab: TabGroup = {
      uid: makeUID('tg', '', '-', true),
      title,
      type: 'tabGroup',
      depth: parent.depth + 1,
      path: parent.path.append(title),
      parent: parent as TabGroup['parent'],
      items: [],
      itemsIndex: {},
    };
    parent.itemsIndex[title] = tab;
    parent.items.push(tab);
    return tab;
  },
  tabLeaf: ({
    title,
    fieldName,
    parent,
    root,
    fieldPathId,
  }: {
    title: string;
    fieldName: string;
    parent: TabGroup | TabRoot;
    root: TabRoot;
    fieldPathId: FieldPathId;
  }) => {
    const tab: TabLeaf = {
      uid: makeUID('tl', '', '-', true),
      fieldPathId,
      title,
      fieldName,
      leafIndex: 0,
      // leafIndex: root.leafTabs.length,
      type: 'tabLeaf',
      depth: parent.depth + 1,
      path: parent.path.append(fieldName),
      parent: parent as TabLeaf['parent'],
    };

    parent.itemsIndex[title] = tab;
    parent.items.push(tab);
    root.leafMap[fieldName] = tab;
    // root.leafTabs.push(tab);

    return tab;
  },
};

function buildTabGroups(schema: TabGroupsJSONSchema) {
  const root = build.tabRoot();

  const allTabGroups: TabGroup[] = [];
  const tabGroupIndex: Record<string, TabGroup> = {};

  Object.values(schema.properties).forEach(fieldSchema => {
    const tabPath = parseTabGroupPath(fieldSchema['ui:tabGroup'], fieldSchema['ui:tabGroupKey'], fieldSchema['ui:tabGroupPath']);
    (fieldSchema as any)[tabGroupPathObjectSymbol] = tabPath;

    tabPath.components.reduce((parent, title) => {
      let node = parent.itemsIndex[title];
      if (!node) {
        const newNode = build.tabGroup({ title, parent });
        allTabGroups.push(newNode);
        tabGroupIndex[newNode.path.key] = newNode;
        node = newNode;
      }

      return node as TabGroup;
    }, root as TabRoot | TabGroup);
  });

  return { root, allTabGroups, tabGroupIndex };
}

function addTabsToTabGroups(schema: TabGroupsJSONSchema, tabGroupIndex: Record<string, TabGroup>, root: TabRoot, fieldPathId: FieldPathId) {
  Object.entries(schema.properties).forEach(([fieldName, fieldSchema]) => {
    // note: this tabPath gets created and assigned in buildTabGroups()
    const tabPath = getTabPathFor(fieldSchema)!;
    build.tabLeaf({
      fieldPathId: { $id: `${fieldPathId.$id}_${fieldName}`, path: [...fieldPathId.path, fieldName] },
      title: fieldSchema['title'] ?? fieldName,
      fieldName,
      parent: tabGroupIndex[tabPath.key],
      root,
    });
  });
}

export function getTabPathFor(tabSchema: TabGroupsJSONSchema['properties'][string]) {
  return (tabSchema as any)[tabGroupPathObjectSymbol] as TabPath | undefined;
}

function sortTabGroups(tabGroups: TabGroup[]) {
  tabGroups.forEach(({ items }) => {
    items.sort(tabGroupsFirst);
  });
}

function tagLeafTabs(tabRoot: TabRoot) {
  const leafTabs = tabRoot.leafTabs;
  const stack: (Tab | TabRoot)[] = [tabRoot];
  while (stack.length) {
    const tab = stack.shift();
    if (tab?.type === 'tabLeaf') {
      tab.leafIndex = leafTabs.length;
      leafTabs.push(tab);
    } else if (tab?.items) {
      stack.unshift(...tab?.items);
    }
  }
}

function tabGroupsFirst({ type: a }: Tab, { type: b }: Tab) {
  if (a === 'tabGroup' && b !== 'tabGroup') return 1;
  if (a !== 'tabGroup' && b !== 'tabGroup') return 0;
  if (a === 'tabGroup' && b === 'tabGroup') return 0;
  return -1;
}

const PATH_DELIMITER = '|||';
const DEFAULT_GROUP = 'General';

class TabPath {
  key: string;
  components: string[];

  constructor(key: string, components: string[]) {
    this.key = key;
    this.components = components;
  }

  static fromComponents(components: string[]) {
    return new TabPath(components.join(PATH_DELIMITER), components);
  }

  static fromKey(key: string) {
    return new TabPath(key, key.split(PATH_DELIMITER));
  }

  static parse(pathDefinition: string | any[]) {
    if (Array.isArray(pathDefinition)) {
      return TabPath.fromComponents(pathDefinition.map(x => `${x}`));
    } else {
      return TabPath.fromKey(pathDefinition);
    }
  }

  append(newComponent: string) {
    const components = [...this.components, newComponent];
    return new TabPath(components.join(PATH_DELIMITER), components);
  }
}

function parseTabGroupPath(tabGroup?: string | string[], key?: string, components?: string[]): TabPath {
  if (tabGroup) {
    if (Array.isArray(tabGroup) && !components) {
      components = tabGroup;
    } else if (!Array.isArray(tabGroup) && !key) {
      key = tabGroup;
    }
  }

  if (components && !Array.isArray(components)) {
    key = `${components}`;
    components = undefined;
  }

  if (key) {
    key = `${key}`;
  }

  // An empty path has no group to hang the tab on, so treat it like no path at all.
  return TabPath.parse(components?.length ? components : key ?? [DEFAULT_GROUP]);
}

export default useTabGroups;
